import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { MultiSelect } from "primereact/multiselect";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import {
  DataTableRowClickEvent,
} from "primereact/datatable";
import { useSelector } from "react-redux";
import { useUserAccess } from "../../../hooks/useUserAccess";
import { useSearchParams } from "react-router-dom";
import {
  dictionaryApi,
  metadataSpellcheckApi,
  MetadataSpellcheckRow,
  MetadataSpellcheckRun,
  DictionaryBlocklistEntry,
} from "../../../api/metadataSpellcheckApi";
import {
  blocklistEntriesToPhraseEntries,
  findFirstBlocklistPhraseInTitle,
  resolveNoSubstituteHitsFromBlocklist,
  resolveSubstitutedHitsFromBlocklist,
  type BlocklistPhraseEntry,
} from "../../../utils/metadataSpellcheckBlocklistMatch";
import "../../YouTubeScorecard/Components/FilterBars/auditFilters.css";
import "./TitleSpellingTab.css";
import { trackToolAction } from "../../../utils/analytics";
import { FULL_PAGE_TABLE_SCROLL_HEIGHT } from "../../../config/tableLayout";

const PAGE_SIZE = 50;
/** First request on mount — keep small for fast time-to-interactive. */
const INITIAL_FETCH_LIMIT = 50;
/** Subsequent "load more" page size. */
const LOAD_MORE_CHUNK = 100;
/** Safety cap on rows held in memory (use filters or export for full sets). */
const MAX_LOADED_ISSUES = 2000;
const MAX_CUSTOM_REJECTION_REASON_LENGTH = 100;
const BULK_COUNTDOWN_MS = 18_000;

type PendingBulkJob =
  | { kind: "accept"; videoIds: string[]; skippedCount: number }
  | {
      kind: "reject";
      videoIds: string[];
      rejectionReason: string | null;
      customRejectionReason: string | null;
    };
const REJECTION_REASON_OPTIONS: { label: string; value: string }[] = [
  { label: "False positive", value: "False positive" },
  { label: "Brand style preference", value: "Brand style preference" },
  { label: "Context-specific wording", value: "Context-specific wording" },
  { label: "Name/term should stay as-is", value: "Name/term should stay as-is" },
  { label: "Other", value: "Other" },
];
const REVIEW_STATUS_OPTIONS: { label: string; value: "pending" | "accepted" | "rejected" }[] =
  [
    { label: "Pending review", value: "pending" },
    { label: "Accepted", value: "accepted" },
    { label: "Rejected", value: "rejected" },
  ];

const VALIDATION_STATUS_OPTIONS: { label: string; value: "needs_manual_review" }[] = [
  { label: "Length validation issues", value: "needs_manual_review" },
];

const formatNumber = (value: number): string =>
  new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(value ?? 0);

type FilterOption = { label: string; value: string };

function mergeFilterOptions(
  selected: string[],
  serverOptions: FilterOption[]
): FilterOption[] {
  const byVal = new Map(serverOptions.map((o) => [o.value, o]));
  for (const v of selected) {
    if (v && !byVal.has(v)) byVal.set(v, { label: v, value: v });
  }
  return Array.from(byVal.values()).sort((a, b) => a.label.localeCompare(b.label));
}

type DiffSpan = { text: string; highlight: boolean };

/** Character-level diff via LCS backtrace: removed/changed chars on original, inserted on corrected. */
function titleDiffSpans(original: string, corrected: string): {
  originalSpans: DiffSpan[];
  correctedSpans: DiffSpan[];
} {
  const m = original.length;
  const n = corrected.length;
  if (m === 0 && n === 0) {
    return { originalSpans: [], correctedSpans: [] };
  }
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        original[i - 1] === corrected[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  const origChars: { ch: string; highlight: boolean }[] = [];
  const corrChars: { ch: string; highlight: boolean }[] = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && original[i - 1] === corrected[j - 1]) {
      origChars.push({ ch: original[i - 1], highlight: false });
      corrChars.push({ ch: corrected[j - 1], highlight: false });
      i -= 1;
      j -= 1;
    } else if (j === 0 || (i > 0 && dp[i - 1][j] >= dp[i][j - 1])) {
      origChars.push({ ch: original[i - 1], highlight: true });
      i -= 1;
    } else {
      corrChars.push({ ch: corrected[j - 1], highlight: true });
      j -= 1;
    }
  }
  origChars.reverse();
  corrChars.reverse();

  const mergeRuns = (parts: { ch: string; highlight: boolean }[]): DiffSpan[] => {
    if (parts.length === 0) return [];
    const out: DiffSpan[] = [];
    let cur: DiffSpan = { text: parts[0].ch, highlight: parts[0].highlight };
    for (let k = 1; k < parts.length; k++) {
      if (parts[k].highlight === cur.highlight) {
        cur.text += parts[k].ch;
      } else {
        out.push(cur);
        cur = { text: parts[k].ch, highlight: parts[k].highlight };
      }
    }
    out.push(cur);
    return out;
  };

  return {
    originalSpans: mergeRuns(origChars),
    correctedSpans: mergeRuns(corrChars),
  };
}

const titleDiffMarkClass = (side: "original" | "corrected"): string =>
  side === "original"
    ? "title-spelling-diff-original"
    : "title-spelling-diff-corrected";

function renderTitleWithDiff(spans: DiffSpan[], side: "original" | "corrected"): React.ReactNode {
  if (spans.length === 0) return "—";
  const markClass = titleDiffMarkClass(side);
  return (
    <span className={markClass}>
      {spans.map((s, idx) =>
        s.highlight ? <mark key={idx}>{s.text}</mark> : <span key={idx}>{s.text}</span>
      )}
    </span>
  );
}

/** Same pattern as YouTube scorecard `metadataTable` ChannelManagerEmail column (comma-separated → one per line). */
function formatChannelManagerEmails(
  value: string | null | undefined
): React.ReactNode {
  if (value === null || value === undefined || value === "") return "—";
  const s = String(value);
  if (!s.includes(",")) {
    return <span className="title-spelling-cell-text">{s}</span>;
  }
  const emails = s.split(",").map((e) => e.trim()).filter(Boolean);
  return <span className="title-spelling-cell-text-multiline">{emails.join("\n")}</span>;
}

function emailsFromCell(value: string | null | undefined): string[] {
  if (value === null || value === undefined || value === "") return [];
  return String(value)
    .split(",")
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
}

function formatUploadTimeDisplay(raw: string | null | undefined): string {
  return formatTimestampDisplay(raw);
}

function formatTimestampDisplay(raw: string | null | undefined): string {
  if (raw == null || String(raw).trim() === "") return "—";
  const d = new Date(String(raw).trim());
  if (Number.isNaN(d.getTime())) return String(raw);
  const pad = (n: number) => String(n).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yy = String(d.getFullYear()).slice(-2);
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${dd}/${mm}/${yy} ${hh}:${min}:${ss}`;
}

function privacyBadgeColor(raw: string | null | undefined): string {
  const value = String(raw ?? "").trim().toLowerCase();
  if (value === "public") return "#22c55e";
  if (value === "private") return "#3b82f6";
  if (value === "unlisted") return "#f97316";
  if (value === "scheduled") return "#f97316";
  return "#ef4444";
}

function formatPrivacyDisplay(raw: string | null | undefined): string {
  const value = String(raw ?? "").trim();
  if (!value) return "—";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function PrivacyStatusCell({ value }: { value: string | null | undefined }) {
  const label = formatPrivacyDisplay(value);
  if (label === "—") return <span>—</span>;
  return (
    <div
      style={{
        backgroundColor: privacyBadgeColor(value),
        color: "#fff",
        textAlign: "center",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </div>
  );
}

function cmpUpdatedAt(
  a: MetadataSpellcheckRow,
  b: MetadataSpellcheckRow,
  order: "asc" | "desc"
): number {
  const ta = Date.parse(a.updated_at ?? "") || 0;
  const tb = Date.parse(b.updated_at ?? "") || 0;
  const c = ta - tb;
  if (c !== 0) return order === "desc" ? -c : c;
  return order === "desc"
    ? b.video_id.localeCompare(a.video_id)
    : a.video_id.localeCompare(b.video_id);
}

function getPriorityLevel(row: MetadataSpellcheckRow): "high" | "secondary" {
  if (row.priority_level === "high" || row.priority_level === "secondary") {
    return row.priority_level;
  }
  const corrections = Array.isArray(row.corrections)
    ? row.corrections.map((c) => String(c).trim().toLowerCase())
    : [];
  return corrections.includes("spelling") ? "high" : "secondary";
}

function needsManualLengthReview(row: MetadataSpellcheckRow): boolean {
  if (row.validation_status === "needs_manual_review") return true;
  if (row.validation_reason === "corrected_title_exceeds_youtube_limit") return true;
  return (row.corrected_title_length ?? 0) > 100;
}

function getReviewStatus(row: MetadataSpellcheckRow): "pending" | "accepted" | "rejected" {
  if (row.review_status === "accepted") return "accepted";
  if (row.review_status === "rejected") return "rejected";
  return "pending";
}

function reviewStatusBadgeClass(status: "pending" | "accepted" | "rejected"): string {
  const base = "title-spelling-status-badge";
  if (status === "accepted") return `${base} title-spelling-review-accepted`;
  if (status === "rejected") return `${base} title-spelling-review-rejected`;
  return `${base} title-spelling-review-pending`;
}

function reviewStatusLabel(status: "pending" | "accepted" | "rejected"): string {
  if (status === "accepted") return "Accepted";
  if (status === "rejected") return "Rejected";
  return "Pending review";
}

function applyStatusLabel(
  row: MetadataSpellcheckRow,
  isAdmin: boolean
): "Queue" | "Updated" | "Failed" | "—" {
  if (row.youtube_apply_status === "succeeded") return "Updated";
  // Failed updates are an admin audit; managers only see what they need to fix.
  if (isAdmin && row.youtube_apply_status === "failed") return "Failed";
  if (
    row.youtube_apply_status === "pending" ||
    row.youtube_apply_status === "processing"
  ) {
    return "Queue";
  }
  return "—";
}

/** Short, readable reason for a failed YouTube update (admin audit). */
function applyFailureReason(error: string | null | undefined): string {
  const e = String(error ?? "");
  if (e.startsWith("videos.list returned no video")) return "Video not found in the CMS";
  if (e.startsWith("Missing content_owner_id")) return "No CMS owner on the issue";
  if (e.startsWith("Corrected title exceeds")) return "Title over 100 characters";
  if (e.startsWith("YouTube title drifted")) return "Title changed on YouTube";
  if (/Forbidden/i.test(e)) return "YouTube refused the update";
  return e ? e.slice(0, 60) : "Unknown error";
}

/** Why an accepted title was sent back to the channel manager after its YouTube update failed. */
function returnedForReviewText(row: MetadataSpellcheckRow): string | null {
  if (row.youtube_apply_status !== "returned_for_review") return null;
  if (row.validation_reason === "youtube_title_changed") {
    return "Sent back: the title was changed on YouTube after this check. Check the live title, then use Edit title.";
  }
  return "Sent back: this title is over YouTube's 100-character limit. Use Edit title to shorten it.";
}

const AUDIT_REVIEW_STATUSES: ("pending" | "accepted" | "rejected")[] = ["pending", "accepted", "rejected"];

function priorityRank(level: "high" | "secondary"): number {
  return level === "high" ? 0 : 1;
}

function isOverLimit(row: MetadataSpellcheckRow): boolean {
  // Keep in sync with server assertIssueEligibleForAccept length checks.
  if (row.validation_reason === "corrected_title_exceeds_youtube_limit") return true;
  const corrected = (row.corrected_title ?? "").trim();
  const len = Math.max(row.corrected_title_length ?? 0, corrected.length);
  return len > 100;
}

function isNoSubstitute(row: MetadataSpellcheckRow): boolean {
  return row.validation_reason === "prohibited_term_no_substitute";
}

function getDisplayCorrectedTitle(row: MetadataSpellcheckRow): string | null {
  const custom = row.custom_corrected_title;
  if (custom != null && String(custom).trim()) return String(custom);
  const corrected = row.corrected_title;
  if (corrected != null && String(corrected).trim()) return String(corrected);
  return null;
}

function resolveNoSubstituteHitsForRow(
  row: MetadataSpellcheckRow,
  blocklist: DictionaryBlocklistEntry[]
): BlocklistPhraseEntry[] {
  const stored = row.blocklist_no_substitute_hits;
  if (stored && stored.length > 0) return stored;
  return resolveNoSubstituteHitsFromBlocklist(row.original_title ?? "", blocklist);
}

function resolveSubstitutedHitsForRow(
  row: MetadataSpellcheckRow,
  blocklist: DictionaryBlocklistEntry[]
): BlocklistPhraseEntry[] {
  const original = row.original_title ?? "";
  const corrected = row.corrected_title ?? "";
  if (!original || !corrected) return [];
  return resolveSubstitutedHitsFromBlocklist(original, corrected, blocklist);
}

function titleEditPrefill(row: MetadataSpellcheckRow): string {
  return row.corrected_title ?? row.original_title ?? "";
}

/**
 * Mirrors server `assertIssueEligibleForAccept` so Accept / Accept Bulk do not
 * schedule rows the API will reject with 400.
 */
function acceptBlockedReason(
  row: MetadataSpellcheckRow,
  blocklist: DictionaryBlocklistEntry[] = []
): string | undefined {
  const corrected = (row.corrected_title ?? "").trim();
  if (!corrected) {
    return "No corrected title to accept.";
  }
  if (isNoSubstitute(row)) {
    return "Prohibited term with no substitute — use Edit title to provide a replacement.";
  }
  if (isOverLimit(row)) {
    const len = Math.max(row.corrected_title_length ?? 0, corrected.length);
    return `Title too long for YouTube (${len} chars). Use Edit title.`;
  }
  if (row.validation_status === "needs_manual_review") {
    return "Issue requires manual review — use Edit title before accepting.";
  }
  if (blocklist.length > 0) {
    const hit = findFirstBlocklistPhraseInTitle(
      corrected,
      blocklistEntriesToPhraseEntries(blocklist)
    );
    if (hit) {
      return `Corrected title still contains prohibited term: ${hit}. Use Edit title.`;
    }
  }
  return undefined;
}

function isBulkAcceptEligible(
  row: MetadataSpellcheckRow,
  blocklist: DictionaryBlocklistEntry[] = []
): boolean {
  return acceptBlockedReason(row, blocklist) === undefined;
}

function partitionBulkAcceptSelection(
  rows: MetadataSpellcheckRow[],
  blocklist: DictionaryBlocklistEntry[] = []
): {
  eligible: MetadataSpellcheckRow[];
  skippedCount: number;
} {
  const eligible = rows.filter((row) => isBulkAcceptEligible(row, blocklist));
  return { eligible, skippedCount: rows.length - eligible.length };
}

function apiErrorDetail(e: unknown): string {
  const ax = e as {
    response?: { data?: { error?: string } };
    message?: string;
  };
  const fromBody = ax.response?.data?.error;
  if (typeof fromBody === "string" && fromBody.trim()) return fromBody.trim();
  if (e instanceof Error && e.message) return e.message;
  return String(e);
}

function resolveLivePendingSelection(
  selected: MetadataSpellcheckRow[],
  allRows: MetadataSpellcheckRow[]
): MetadataSpellcheckRow[] {
  return selected
    .map((selectedRow) => allRows.find((row) => row.video_id === selectedRow.video_id))
    .filter(
      (row): row is MetadataSpellcheckRow =>
        row != null && getReviewStatus(row) === "pending"
    );
}

function getDraftedRejectionReason(row: MetadataSpellcheckRow): string | null {
  const drafted = row._rejectionReasonDraft;
  if (typeof drafted === "string") {
    const trimmed = drafted.trim();
    return trimmed ? trimmed : null;
  }
  const existing = row.rejection_reason;
  if (typeof existing === "string") {
    const trimmed = existing.trim();
    return trimmed ? trimmed : null;
  }
  return null;
}

function getDraftedCustomRejectionReason(row: MetadataSpellcheckRow): string | null {
  const drafted = row._customRejectionReasonDraft;
  if (typeof drafted === "string") {
    return drafted;
  }
  const existing = row.custom_rejection_reason;
  if (typeof existing === "string") {
    return existing;
  }
  return null;
}

interface KpiCardProps {
  label: string;
  value: string;
  subValue: string;
  accentColor: string;
}

/** Shown on load; optional columns are added via the "Additional columns" multiselect. */
const DEFAULT_TITLE_SPELLING_TABLE_COLUMNS: readonly string[] = [
  "video_id",
  "channel_title",
  "channel_language",
  "video_type",
  "video_privacy_status",
  "upload_time",
  "priority_level",
  "original_title",
  "corrected_title",
  "corrections",
  "review_status",
  "review_actions",
  "apply_status",
] as const;

const OPTIONAL_TITLE_SPELLING_TABLE_COLUMNS: { id: string; label: string }[] = [
  { id: "run_id", label: "Last run ID" },
  { id: "channel_id", label: "Channel ID" },
  { id: "vod_channel_manager_email", label: "VOD mgr email" },
  { id: "shorts_channel_manager_email", label: "Shorts mgr email" },
  { id: "updated_at", label: "Updated" },
];

const KpiCard = ({ label, value, subValue, accentColor }: KpiCardProps) => (
  <div className="title-spelling-kpi-card" style={{ ["--kpi-accent" as string]: accentColor }}>
    <p className="title-spelling-kpi-label">{label}</p>
    <p className="title-spelling-kpi-value">{value}</p>
    <p className="title-spelling-kpi-sub">{subValue}</p>
  </div>
);

const TitleSpellingTab = () => {
  const toast = useRef<Toast>(null);
  const [searchParams] = useSearchParams();
  const user = useSelector((state: any) => state.googleAuth.user);
  const loggedInUserEmail = String(user?.email ?? "").trim().toLowerCase();
  const { isAdmin } = useUserAccess();

  const [metrics, setMetrics] = useState<{
    totalOpenIssues: number;
    totalAcceptedIssues: number;
    totalRejectedIssues: number;
    totalMyOpenIssues?: number;
  } | null>(null);
  const [latestRunSummary, setLatestRunSummary] = useState<{
    newIssues: number;
    runId?: string;
  }>({
    newIssues: 0,
  });
  const [allRows, setAllRows] = useState<MetadataSpellcheckRow[]>([]);
  const [loadedCount, setLoadedCount] = useState(0);
  const [issuesNextCursor, setIssuesNextCursor] = useState<string | undefined>();
  const [issuesHasMore, setIssuesHasMore] = useState(false);
  const [totalMatchingServer, setTotalMatchingServer] = useState<number | undefined>();
  const [loadCapReached, setLoadCapReached] = useState(false);
  const [filterOptions, setFilterOptions] = useState<{
    languages: FilterOption[];
    videoTypes: FilterOption[];
    videoPrivacyStatuses: FilterOption[];
    channelManagers: FilterOption[];
    channels: FilterOption[];
    uploadDates: FilterOption[];
    correctionTypes: FilterOption[];
  }>({
    languages: [],
    videoTypes: [],
    videoPrivacyStatuses: [],
    channelManagers: [],
    channels: [],
    uploadDates: [],
    correctionTypes: [],
  });
  const [blocklistEntries, setBlocklistEntries] = useState<DictionaryBlocklistEntry[]>([]);

  const [pageIndex, setPageIndex] = useState(0);
  const [updatedSortOrder, setUpdatedSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [selectedManagerEmails, setSelectedManagerEmails] = useState<string[]>([]);
  const [selectedVideoTypes, setSelectedVideoTypes] = useState<string[]>([]);
  const [selectedVideoPrivacyStatuses, setSelectedVideoPrivacyStatuses] = useState<string[]>([]);
  const [selectedCorrectionTypes, setSelectedCorrectionTypes] = useState<string[]>([]);
  const [selectedReviewStatuses, setSelectedReviewStatuses] = useState<
    ("pending" | "accepted" | "rejected")[]
  >(["pending"]);
  const [selectedValidationStatuses, setSelectedValidationStatuses] = useState<string[]>([]);
  const [selectedChannelKeys, setSelectedChannelKeys] = useState<string[]>(
    () => {
      const keys = searchParams.get("channelKeys");
      return keys
        ? keys.split(",").map((k) => k.trim()).filter(Boolean)
        : [];
    }
  );
  const [selectedUploadDates, setSelectedUploadDates] = useState<string[]>([]);
  const [myIssuesOnly, setMyIssuesOnly] = useState<boolean>(() => {
    if (searchParams.get("myIssues") === "0") return false;
    return Boolean(loggedInUserEmail);
  });
  const [auditFailedUpdates, setAuditFailedUpdates] = useState<boolean>(
    () => searchParams.get("audit") === "failedUpdates"
  );
  // Admin-only audit of accepted titles whose YouTube update failed.
  const auditActive = isAdmin && auditFailedUpdates;
  const [optionalTableColumns, setOptionalTableColumns] = useState<string[]>([]);

  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingTitleConfirmation, setSavingTitleConfirmation] = useState<
    Record<string, boolean>
  >({});
  const [selectedRows, setSelectedRows] = useState<MetadataSpellcheckRow[]>([]);
  const [pendingBulkJob, setPendingBulkJob] = useState<PendingBulkJob | null>(null);
  const [bulkExecuting, setBulkExecuting] = useState(false);
  const [selectionFrozen, setSelectionFrozen] = useState(false);
  const [bulkRejectDialogVisible, setBulkRejectDialogVisible] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState<string | null>(null);
  const [bulkRejectCustomReason, setBulkRejectCustomReason] = useState("");
  const [bulkPendingKey, setBulkPendingKey] = useState(0);

  const bulkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingBulkJobRef = useRef<PendingBulkJob | null>(null);
  const allRowsRef = useRef(allRows);
  const bulkGuardRef = useRef(0);
  const selectionLockedRef = useRef(false);
  const bulkBusyRef = useRef(false);
  const isMountedRef = useRef(true);
  const skipBulkCancelOnFilterRef = useRef(true);
  const issuesRequestSeqRef = useRef(0);
  const selectedReviewStatusesRef = useRef(selectedReviewStatuses);
  selectedReviewStatusesRef.current = selectedReviewStatuses;

  useEffect(() => {
    allRowsRef.current = allRows;
  }, [allRows]);

  const loadMetrics = useCallback(async () => {
    try {
      setError(null);
      const m = await metadataSpellcheckApi.getOpenIssuesMetrics(
        loggedInUserEmail ? { managerEmail: loggedInUserEmail } : undefined
      );
      setMetrics(m);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError("Unable to load issue totals right now.");
      toast.current?.show({
        severity: "error",
        summary: "Failed to load totals",
        detail: msg,
      });
    }
  }, [loggedInUserEmail]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const [options, blocklistRes] = await Promise.all([
        metadataSpellcheckApi.getIssueFilterOptions(),
        dictionaryApi.getBlocklist(),
      ]);
      setFilterOptions(options);
      setBlocklistEntries(blocklistRes.entries ?? []);
    } catch {
      // Filter dropdowns fall back to values from loaded rows.
    }
  }, []);

  const fetchIssuesPage = useCallback(
    async (options: {
      startAfter?: string;
      limit: number;
      append: boolean;
    }) => {
      const managerEmail =
        myIssuesOnly && loggedInUserEmail && !auditActive ? loggedInUserEmail : undefined;
      const requestSeq = ++issuesRequestSeqRef.current;
      const res = await metadataSpellcheckApi.listOpenIssues({
        limit: options.limit,
        startAfter: options.startAfter,
        sortBy: "updated_at",
        sortOrder: updatedSortOrder,
        reviewStatuses: auditActive ? AUDIT_REVIEW_STATUSES : selectedReviewStatuses,
        youtubeApplyStatuses: auditActive ? ["failed"] : undefined,
        managerEmail,
        managerEmails:
          !managerEmail && selectedManagerEmails.length > 0
            ? selectedManagerEmails
            : undefined,
        languages: selectedLanguages.length > 0 ? selectedLanguages : undefined,
        videoTypes: selectedVideoTypes.length > 0 ? selectedVideoTypes : undefined,
        videoPrivacyStatuses:
          selectedVideoPrivacyStatuses.length > 0 ? selectedVideoPrivacyStatuses : undefined,
        channelKeys: selectedChannelKeys.length > 0 ? selectedChannelKeys : undefined,
        correctionTypes:
          selectedCorrectionTypes.length > 0 ? selectedCorrectionTypes : undefined,
        uploadDates: selectedUploadDates.length > 0 ? selectedUploadDates : undefined,
        validationStatuses:
          selectedValidationStatuses.length > 0 ? selectedValidationStatuses : undefined,
      });
      // A newer request (e.g. sort toggled again) supersedes this one.
      if (requestSeq !== issuesRequestSeqRef.current) return res;
      setIssuesNextCursor(res.nextStartAfter);
      setIssuesHasMore(Boolean(res.nextStartAfter));
      if (res.totalMatching !== undefined) {
        setTotalMatchingServer(res.totalMatching);
      } else {
        setTotalMatchingServer(undefined);
      }
      setAllRows((prev) => {
        const merged = options.append ? [...prev, ...res.rows] : res.rows;
        const capped = merged.slice(0, MAX_LOADED_ISSUES);
        setLoadedCount(capped.length);
        setLoadCapReached(merged.length >= MAX_LOADED_ISSUES);
        if (merged.length >= MAX_LOADED_ISSUES) {
          setIssuesHasMore(false);
        }
        return capped;
      });
      return res;
    },
    [
      auditActive,
      loggedInUserEmail,
      myIssuesOnly,
      selectedReviewStatuses,
      selectedLanguages,
      selectedManagerEmails,
      selectedVideoTypes,
      selectedVideoPrivacyStatuses,
      selectedCorrectionTypes,
      selectedValidationStatuses,
      selectedChannelKeys,
      selectedUploadDates,
      updatedSortOrder,
    ]
  );

  const loadIssuesFirstPage = useCallback(async () => {
    setLoadingInitial(true);
    setLoadCapReached(false);
    setIssuesNextCursor(undefined);
    setIssuesHasMore(false);
    try {
      await fetchIssuesPage({
        limit: INITIAL_FETCH_LIMIT,
        append: false,
      });
      setError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setAllRows([]);
      setLoadedCount(0);
      setError("Unable to load issues right now.");
      toast.current?.show({
        severity: "error",
        summary: "Failed to load issues",
        detail: msg,
      });
    } finally {
      setLoadingInitial(false);
    }
  }, [fetchIssuesPage]);

  const loadMoreIssues = useCallback(async () => {
    if (!issuesHasMore || loadingMore || loadCapReached || !issuesNextCursor) return;
    setLoadingMore(true);
    try {
      await fetchIssuesPage({
        startAfter: issuesNextCursor,
        limit: LOAD_MORE_CHUNK,
        append: true,
      });
      setError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.current?.show({
        severity: "error",
        summary: "Failed to load more issues",
        detail: msg,
      });
    } finally {
      setLoadingMore(false);
    }
  }, [fetchIssuesPage, issuesHasMore, issuesNextCursor, loadCapReached, loadingMore]);

  const loadLatestRunSummary = useCallback(async () => {
    try {
      const runsRes = await metadataSpellcheckApi.listRuns(1);
      const latestRun: MetadataSpellcheckRun | undefined = runsRes.runs?.[0];
      if (!latestRun) {
        setLatestRunSummary({ newIssues: 0 });
        return;
      }
      const rowCounts = latestRun.rowCounts;
      const newIssues = rowCounts?.openIssuesWritten ?? 0;
      setLatestRunSummary({
        newIssues,
        runId: latestRun.runId,
      });
    } catch {
      setLatestRunSummary({ newIssues: 0 });
    }
  }, []);

  useEffect(() => {
    void Promise.all([loadMetrics(), loadLatestRunSummary(), loadFilterOptions()]);
  }, [loadMetrics, loadLatestRunSummary, loadFilterOptions]);

  const didDefaultMyIssues = useRef(false);
  useEffect(() => {
    if (!loggedInUserEmail || didDefaultMyIssues.current) return;
    didDefaultMyIssues.current = true;
    if (searchParams.get("myIssues") === "0") return;
    setMyIssuesOnly(true);
  }, [loggedInUserEmail, searchParams]);

  useEffect(() => {
    void loadIssuesFirstPage();
  }, [loadIssuesFirstPage]);

  useEffect(() => {
    setPageIndex(0);
  }, [
    selectedReviewStatuses,
    selectedLanguages,
    selectedManagerEmails,
    selectedVideoTypes,
    selectedVideoPrivacyStatuses,
    selectedCorrectionTypes,
    selectedValidationStatuses,
    selectedChannelKeys,
    selectedUploadDates,
    myIssuesOnly,
    auditActive,
  ]);

  const languageOptions = useMemo(
    () => mergeFilterOptions(selectedLanguages, filterOptions.languages),
    [filterOptions.languages, selectedLanguages]
  );

  const videoTypeOptions = useMemo(
    () => mergeFilterOptions(selectedVideoTypes, filterOptions.videoTypes),
    [filterOptions.videoTypes, selectedVideoTypes]
  );

  const videoPrivacyOptions = useMemo(
    () =>
      mergeFilterOptions(selectedVideoPrivacyStatuses, filterOptions.videoPrivacyStatuses),
    [filterOptions.videoPrivacyStatuses, selectedVideoPrivacyStatuses]
  );

  const correctionTypeOptions = useMemo(
    () => mergeFilterOptions(selectedCorrectionTypes, filterOptions.correctionTypes),
    [filterOptions.correctionTypes, selectedCorrectionTypes]
  );

  const channelOptions = useMemo(
    () => mergeFilterOptions(selectedChannelKeys, filterOptions.channels),
    [filterOptions.channels, selectedChannelKeys]
  );

  const uploadDateOptions = useMemo(
    () => mergeFilterOptions(selectedUploadDates, filterOptions.uploadDates),
    [filterOptions.uploadDates, selectedUploadDates]
  );

  const channelManagerOptionsFromIssues = useMemo(
    () => mergeFilterOptions(selectedManagerEmails, filterOptions.channelManagers),
    [filterOptions.channelManagers, selectedManagerEmails]
  );

  const myIssuesRows = useMemo(() => {
    if (!loggedInUserEmail) return [];
    return allRows.filter((row) => {
      const emails = new Set([
        ...emailsFromCell(row.vod_channel_manager_email),
        ...emailsFromCell(row.shorts_channel_manager_email),
      ]);
      return emails.has(loggedInUserEmail);
    });
  }, [allRows, loggedInUserEmail]);

  const sortedRows = useMemo(() => {
    const arr = [...allRows];
    arr.sort((a, b) => {
      const byPriority =
        priorityRank(getPriorityLevel(a)) - priorityRank(getPriorityLevel(b));
      if (byPriority !== 0) return byPriority;
      return cmpUpdatedAt(a, b, updatedSortOrder);
    });
    return arr;
  }, [allRows, updatedSortOrder]);

  const pageRows = useMemo(
    () => sortedRows.slice(pageIndex * PAGE_SIZE, pageIndex * PAGE_SIZE + PAGE_SIZE),
    [sortedRows, pageIndex]
  );

  const patchRowByVideoId = useCallback(
    (videoId: string, updater: (row: MetadataSpellcheckRow) => MetadataSpellcheckRow) => {
      setAllRows((rows) => rows.map((r) => (r.video_id === videoId ? updater(r) : r)));
    },
    []
  );

  /**
   * Applies a saved review decision in place. Rows whose new status falls outside
   * the Review state filter are dropped so the reviewer's position holds and the
   * next row slides up, rather than the row lingering or re-sorting elsewhere.
   */
  const settleReviewedRow = useCallback(
    (videoId: string, updater: (row: MetadataSpellcheckRow) => MetadataSpellcheckRow) => {
      setAllRows((rows) =>
        rows.flatMap((r) => {
          if (r.video_id !== videoId) return [r];
          const next = updater(r);
          return selectedReviewStatusesRef.current.includes(getReviewStatus(next))
            ? [next]
            : [];
        })
      );
    },
    []
  );

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(sortedRows.length / PAGE_SIZE) - 1);
    if (pageIndex > lastPage) setPageIndex(lastPage);
  }, [pageIndex, sortedRows.length]);

  const bulkBusy = pendingBulkJob !== null || bulkExecuting;
  const selectionLocked = bulkBusy || selectionFrozen;
  selectionLockedRef.current = selectionLocked;
  bulkBusyRef.current = bulkBusy;

  const liveSelectedPendingRows = useMemo(
    () => resolveLivePendingSelection(selectedRows, allRows),
    [selectedRows, allRows]
  );
  const liveSelectedPendingCount = liveSelectedPendingRows.length;

  const pendingBulkVideoIds = useMemo(() => {
    if (!pendingBulkJob) return new Set<string>();
    return new Set(pendingBulkJob.videoIds);
  }, [pendingBulkJob]);

  useEffect(() => {
    pendingBulkJobRef.current = pendingBulkJob;
  }, [pendingBulkJob]);

  const clearSelection = useCallback(() => {
    setSelectedRows([]);
  }, []);

  const deselectRowByVideoId = useCallback((videoId: string) => {
    setSelectedRows((prev) =>
      prev.some((r) => r.video_id === videoId)
        ? prev.filter((r) => r.video_id !== videoId)
        : prev
    );
  }, []);

  const resetBulkRejectDialog = useCallback(() => {
    setBulkRejectDialogVisible(false);
    setBulkRejectReason(null);
    setBulkRejectCustomReason("");
  }, []);

  useEffect(() => {
    setSelectedRows((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.filter((selected) => {
        const live = allRows.find((r) => r.video_id === selected.video_id);
        return live != null && getReviewStatus(live) === "pending";
      });
      return next.length === prev.length ? prev : next;
    });
  }, [allRows]);

  const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = "";
  }, []);

  const cancelPendingBulk = useCallback(() => {
    if (bulkTimerRef.current) {
      clearTimeout(bulkTimerRef.current);
      bulkTimerRef.current = null;
    }
    window.removeEventListener("beforeunload", handleBeforeUnload);
    setPendingBulkJob(null);
    if (!bulkExecuting) {
      setSelectionFrozen(false);
    }
  }, [bulkExecuting, handleBeforeUnload]);

  const interruptBulkWorkflow = useCallback(() => {
    bulkGuardRef.current += 1;
    cancelPendingBulk();
    clearSelection();
    resetBulkRejectDialog();
    setSelectionFrozen(false);
  }, [cancelPendingBulk, clearSelection, resetBulkRejectDialog]);

  const executePendingBulk = useCallback(async () => {
    const job = pendingBulkJobRef.current;
    if (!job || !isMountedRef.current) return;

    if (bulkTimerRef.current) {
      clearTimeout(bulkTimerRef.current);
      bulkTimerRef.current = null;
    }
    window.removeEventListener("beforeunload", handleBeforeUnload);
    setPendingBulkJob(null);
    setBulkExecuting(true);

    let succeeded = 0;
    let failed = 0;
    let skippedStale = 0;
    const failureSamples: string[] = [];

    try {
      if (job.kind === "accept") {
        for (const vid of job.videoIds) {
          const live = allRowsRef.current.find((row) => row.video_id === vid);
          if (!live || getReviewStatus(live) !== "pending") {
            skippedStale += 1;
            continue;
          }
          try {
            await metadataSpellcheckApi.postAcceptIssue(vid);
            succeeded += 1;
            const now = new Date().toISOString();
            settleReviewedRow(vid, (current) => ({
              ...current,
              review_status: "accepted",
              suggested_title_confirmation: true,
              accepted_recorded_at: now,
              rejection_reason: null,
              custom_rejection_reason: null,
              youtube_apply_status: "pending",
              is_active_issue: false,
            }));
          } catch (e) {
            failed += 1;
            if (failureSamples.length < 3) {
              failureSamples.push(`${vid}: ${apiErrorDetail(e)}`);
            }
          }
        }
        let detail = `${succeeded} accepted.`;
        if (job.skippedCount > 0) {
          detail += ` ${job.skippedCount} skipped — need manual review (length limit or no substitute).`;
        }
        if (skippedStale > 0) {
          detail += ` ${skippedStale} skipped — no longer pending review.`;
        }
        if (failed > 0) {
          detail += ` ${failed} failed — try again individually.`;
          if (failureSamples.length > 0) {
            detail += ` Examples: ${failureSamples.join("; ")}`;
          }
        }
        toast.current?.show({
          severity: failed > 0 ? "warn" : "success",
          summary: "Bulk accept complete",
          detail,
          life: failed > 0 ? 12000 : 6000,
        });
      } else {
        for (const vid of job.videoIds) {
          const live = allRowsRef.current.find((row) => row.video_id === vid);
          if (!live || getReviewStatus(live) !== "pending") {
            skippedStale += 1;
            continue;
          }
          try {
            await metadataSpellcheckApi.postRejectIssue(vid, {
              rejection_reason: job.rejectionReason,
              custom_rejection_reason: job.customRejectionReason,
            });
            succeeded += 1;
            const now = new Date().toISOString();
            settleReviewedRow(vid, (current) => ({
              ...current,
              review_status: "rejected",
              suggested_title_confirmation: false,
              rejection_recorded_at: now,
              rejection_reason: job.rejectionReason,
              custom_rejection_reason: job.customRejectionReason,
              is_active_issue: false,
            }));
          } catch (e) {
            failed += 1;
            if (failureSamples.length < 3) {
              failureSamples.push(`${vid}: ${apiErrorDetail(e)}`);
            }
          }
        }
        let detail =
          failed > 0
            ? `${succeeded} rejected. ${failed} failed — try again individually.`
            : `${succeeded} rejected.`;
        if (skippedStale > 0) {
          detail += ` ${skippedStale} skipped — no longer pending review.`;
        }
        if (failureSamples.length > 0) {
          detail += ` Examples: ${failureSamples.join("; ")}`;
        }
        toast.current?.show({
          severity: failed > 0 ? "warn" : "success",
          summary: "Bulk reject complete",
          detail,
          life: failed > 0 ? 12000 : 6000,
        });
      }
      void loadMetrics();
      void loadLatestRunSummary();
    } finally {
      if (isMountedRef.current) {
        setBulkExecuting(false);
        setSelectionFrozen(false);
        clearSelection();
      }
    }
  }, [
    clearSelection,
    handleBeforeUnload,
    loadLatestRunSummary,
    loadMetrics,
    settleReviewedRow,
  ]);

  const schedulePendingBulk = useCallback(
    (job: PendingBulkJob) => {
      if (bulkTimerRef.current) {
        clearTimeout(bulkTimerRef.current);
      }
      window.removeEventListener("beforeunload", handleBeforeUnload);

      pendingBulkJobRef.current = job;
      setAllRows((rows) =>
        rows.map((row) => (row._titleEditOpen ? { ...row, _titleEditOpen: false } : row))
      );
      setPendingBulkJob(job);
      setSelectionFrozen(true);
      setBulkPendingKey((key) => key + 1);
      window.addEventListener("beforeunload", handleBeforeUnload);

      bulkTimerRef.current = setTimeout(() => {
        void executePendingBulk();
      }, BULK_COUNTDOWN_MS);
    },
    [executePendingBulk, handleBeforeUnload]
  );

  const handleBulkAccept = useCallback(() => {
    if (liveSelectedPendingCount === 0 || bulkBusy) return;
    const guardAtOpen = bulkGuardRef.current;
    const { eligible, skippedCount } = partitionBulkAcceptSelection(
      liveSelectedPendingRows,
      blocklistEntries
    );
    if (eligible.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Nothing to accept",
        detail: "All selected rows require title edit before they can be bulk accepted.",
        life: 4000,
      });
      return;
    }
    setSelectionFrozen(true);
    confirmDialog({
      className: "title-spelling-bulk-confirm-dialog",
      header: "Accept bulk corrections",
      message: (
        <div className="title-spelling-bulk-confirm-body">
          <p>
            <strong>{eligible.length}</strong> issue{eligible.length === 1 ? "" : "s"} will be
            accepted
            {skippedCount > 0 ? (
              <>
                ; <strong>{skippedCount}</strong> will be skipped (require title edit)
              </>
            ) : null}
            .
          </p>
          <p>
            Actions run after a short countdown — you can undo before then. YouTube title apply
            queues per video when the countdown finishes.
          </p>
        </div>
      ),
      icon: "pi pi-check-circle",
      acceptClassName: "p-button-success",
      acceptLabel: "Schedule accept",
      rejectLabel: "Cancel",
      accept: () => {
        if (guardAtOpen !== bulkGuardRef.current) return;
        trackToolAction("MetadataAudit", "bulk_accept");
        schedulePendingBulk({
          kind: "accept",
          videoIds: eligible.map((r) => r.video_id),
          skippedCount,
        });
      },
      reject: () => {
        setSelectionFrozen(false);
      },
      onHide: () => {
        if (!pendingBulkJobRef.current && !bulkExecuting) {
          setSelectionFrozen(false);
        }
      },
    });
  }, [
    blocklistEntries,
    bulkBusy,
    bulkExecuting,
    liveSelectedPendingCount,
    liveSelectedPendingRows,
    schedulePendingBulk,
  ]);

  const handleBulkRejectConfirm = useCallback(() => {
    if (liveSelectedPendingCount === 0 || bulkBusy) return;
    const trimmedCustom = bulkRejectCustomReason.trim();
    if (bulkRejectReason === "Other" && !trimmedCustom) {
      toast.current?.show({
        severity: "warn",
        summary: "Custom reason required",
        detail: "Enter a custom rejection reason when selecting Other.",
        life: 3000,
      });
      return;
    }
    if (trimmedCustom.length > MAX_CUSTOM_REJECTION_REASON_LENGTH) {
      toast.current?.show({
        severity: "warn",
        summary: "Custom reason too long",
        detail: `Custom rejection reason must be ${MAX_CUSTOM_REJECTION_REASON_LENGTH} characters or fewer.`,
        life: 3000,
      });
      return;
    }
    const guardAtOpen = bulkGuardRef.current;
    const videoIds = liveSelectedPendingRows.map((r) => r.video_id);
    const rejectionReason = bulkRejectReason?.trim() ? bulkRejectReason.trim() : null;
    const customRejectionReason = trimmedCustom ? trimmedCustom : null;

    setBulkRejectDialogVisible(false);
    setSelectionFrozen(true);
    confirmDialog({
      className: "title-spelling-bulk-confirm-dialog",
      header: "Reject bulk corrections",
      message: (
        <div className="title-spelling-bulk-confirm-body">
          <p>
            <strong>{videoIds.length}</strong> issue{videoIds.length === 1 ? "" : "s"} will be
            rejected
            {rejectionReason ? (
              <>
                {" "}
                with reason: <strong>{rejectionReason}</strong>
              </>
            ) : null}
            .
          </p>
          {rejectionReason === "Other" && customRejectionReason ? (
            <p className="title-spelling-bulk-confirm-description">
              Description: <strong>{customRejectionReason}</strong>
            </p>
          ) : null}
          <p>
            Actions run after a short countdown — you can undo before then.
          </p>
        </div>
      ),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Schedule reject",
      rejectLabel: "Cancel",
      accept: () => {
        if (guardAtOpen !== bulkGuardRef.current) return;
        trackToolAction("MetadataAudit", "bulk_reject");
        schedulePendingBulk({
          kind: "reject",
          videoIds,
          rejectionReason,
          customRejectionReason,
        });
      },
      reject: () => {
        setSelectionFrozen(false);
      },
      onHide: () => {
        if (!pendingBulkJobRef.current && !bulkExecuting) {
          setSelectionFrozen(false);
        }
      },
    });
  }, [
    bulkBusy,
    bulkExecuting,
    bulkRejectCustomReason,
    bulkRejectReason,
    liveSelectedPendingCount,
    liveSelectedPendingRows,
    schedulePendingBulk,
  ]);

  const handleRowClick = useCallback(
    (e: DataTableRowClickEvent) => {
      if (selectionLocked) return;
      const row = e.data as MetadataSpellcheckRow;
      if (getReviewStatus(row) !== "pending") return;
      setSelectedRows((prev) => {
        const exists = prev.some((r) => r.video_id === row.video_id);
        if (exists) {
          return prev.filter((r) => r.video_id !== row.video_id);
        }
        return [...prev, row];
      });
    },
    [selectionLocked]
  );

  const handleSelectionChange = useCallback(
    (e: { value: unknown }) => {
      if (selectionLocked) return;
      const value = e.value;
      setSelectedRows(Array.isArray(value) ? (value as MetadataSpellcheckRow[]) : []);
    },
    [selectionLocked]
  );

  const isRowSelectable = useCallback(
    (event: { data: unknown }) => {
      const row = event.data as MetadataSpellcheckRow;
      return getReviewStatus(row) === "pending" && !selectionLocked;
    },
    [selectionLocked]
  );

  const spellingRowClassName = useCallback(
    (row: MetadataSpellcheckRow) => {
      const classes: string[] = [];
      if (getReviewStatus(row) === "pending" && !selectionLocked) {
        classes.push("title-spelling-row-selectable");
      }
      if (pendingBulkVideoIds.has(row.video_id)) {
        classes.push("title-spelling-row-bulk-pending");
      }
      if (savingTitleConfirmation[row.video_id] || row._savingTitleEdit) {
        classes.push("title-spelling-row-saving");
      }
      return classes.join(" ");
    },
    [pendingBulkVideoIds, savingTitleConfirmation, selectionLocked]
  );

  useEffect(() => {
    if (skipBulkCancelOnFilterRef.current) {
      skipBulkCancelOnFilterRef.current = false;
      return;
    }
    interruptBulkWorkflow();
  }, [
    interruptBulkWorkflow,
    selectedReviewStatuses,
    selectedLanguages,
    selectedManagerEmails,
    selectedVideoTypes,
    selectedVideoPrivacyStatuses,
    selectedCorrectionTypes,
    selectedValidationStatuses,
    selectedChannelKeys,
    selectedUploadDates,
    myIssuesOnly,
    auditActive,
    updatedSortOrder,
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cancelPendingBulk();
    };
  }, [cancelPendingBulk]);

  const onPage = (e: PaginatorPageChangeEvent) => {
    interruptBulkWorkflow();
    setPageIndex(Math.floor((e.first ?? 0) / PAGE_SIZE));
  };

  const onToggleUpdatedSortOrder = useCallback(() => {
    setUpdatedSortOrder((s) => (s === "desc" ? "asc" : "desc"));
    setPageIndex(0);
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedReviewStatuses(["pending"]);
    setSelectedLanguages([]);
    setSelectedManagerEmails([]);
    setSelectedVideoTypes([]);
    setSelectedVideoPrivacyStatuses([]);
    setSelectedCorrectionTypes([]);
    setSelectedValidationStatuses([]);
    setSelectedChannelKeys([]);
    setSelectedUploadDates([]);
    setAuditFailedUpdates(false);
  }, []);

  const onMyIssuesOnlyChange = useCallback(
    (enabled: boolean) => {
      setMyIssuesOnly(enabled);
      if (enabled) {
        setSelectedManagerEmails([]);
      }
    },
    []
  );

  const onSaveTitleReview = useCallback(
    async (row: MetadataSpellcheckRow, confirmed: boolean) => {
      if (selectionLockedRef.current) return;
      const vid = row.video_id;
      const rejectionReason = confirmed ? null : getDraftedRejectionReason(row);
      const rawCustomRejectionReason = confirmed
        ? null
        : getDraftedCustomRejectionReason(row);
      const customRejectionReason =
        rawCustomRejectionReason && rawCustomRejectionReason.trim()
          ? rawCustomRejectionReason.trim()
          : null;
      if (
        !confirmed &&
        customRejectionReason != null &&
        customRejectionReason.length > MAX_CUSTOM_REJECTION_REASON_LENGTH
      ) {
        toast.current?.show({
          severity: "warn",
          summary: "Custom reason too long",
          detail: `Custom rejection reason must be ${MAX_CUSTOM_REJECTION_REASON_LENGTH} characters or fewer.`,
          life: 3000,
        });
        return;
      }
      if (!confirmed && rejectionReason === "Other" && !customRejectionReason) {
        toast.current?.show({
          severity: "warn",
          summary: "Custom reason required",
          detail: "Enter a custom rejection reason when selecting Other.",
          life: 3000,
        });
        return;
      }
      trackToolAction("MetadataAudit", confirmed ? "accept" : "reject");
      deselectRowByVideoId(vid);
      // The row stays put (buttons disabled) until the API confirms, then
      // settleReviewedRow removes it from a pending-only view. updated_at is
      // deliberately not bumped locally: the table sorts by it, so doing so
      // would move the row to the top/bottom of the list mid-review.
      setSavingTitleConfirmation((s) => ({ ...s, [vid]: true }));
      try {
        if (confirmed) {
          await metadataSpellcheckApi.postAcceptIssue(vid);
        } else {
          await metadataSpellcheckApi.postRejectIssue(vid, {
            rejection_reason: rejectionReason,
            custom_rejection_reason: customRejectionReason,
          });
        }
        const now = new Date().toISOString();
        settleReviewedRow(vid, (current) => ({
          ...current,
          review_status: confirmed ? "accepted" : "rejected",
          suggested_title_confirmation: confirmed,
          accepted_recorded_at: confirmed ? now : current.accepted_recorded_at ?? null,
          rejection_recorded_at: confirmed ? current.rejection_recorded_at ?? null : now,
          rejection_reason: confirmed ? null : rejectionReason,
          custom_rejection_reason: confirmed ? null : customRejectionReason,
          _rejectionReasonDraft: undefined,
          _customRejectionReasonDraft: undefined,
          youtube_apply_status: confirmed ? "pending" : current.youtube_apply_status ?? null,
          is_active_issue: false,
        }));
        void loadMetrics();
        void loadLatestRunSummary();
        toast.current?.show({
          severity: "success",
          summary: "Decision saved",
          detail: confirmed
            ? "Acceptance recorded for this suggestion."
            : "Rejection recorded for this suggestion.",
          life: 3200,
        });
      } catch (e) {
        toast.current?.show({
          severity: "error",
          summary: "Could not complete review",
          detail: apiErrorDetail(e),
        });
      } finally {
        setSavingTitleConfirmation((s) => ({ ...s, [vid]: false }));
      }
    },
    [deselectRowByVideoId, loadMetrics, loadLatestRunSummary, settleReviewedRow]
  );

  const handleOpenTitleEdit = useCallback((row: MetadataSpellcheckRow) => {
    if (selectionLockedRef.current) return;
    const vid = row.video_id;
    setAllRows((rows) =>
      rows.map((r) => ({
        ...r,
        _titleEditOpen: r.video_id === vid,
        ...(r.video_id === vid ? { _titleEditDraft: titleEditPrefill(r) } : {}),
      }))
    );
  }, []);

  const onSaveEditedTitle = useCallback(
    async (row: MetadataSpellcheckRow, customTitle: string) => {
      if (selectionLockedRef.current) return;
      const vid = row.video_id;
      const trimmed = customTitle.trim();
      if (!trimmed || trimmed.length > 100) return;
      const blocklistHit = findFirstBlocklistPhraseInTitle(
        trimmed,
        blocklistEntriesToPhraseEntries(blocklistEntries)
      );
      if (blocklistHit) return;
      deselectRowByVideoId(vid);
      patchRowByVideoId(vid, (current) => ({ ...current, _savingTitleEdit: true }));
      try {
        await metadataSpellcheckApi.postCustomFix(vid, trimmed);
        const now = new Date().toISOString();
        settleReviewedRow(vid, (r) => ({
          ...r,
          _savingTitleEdit: false,
          _titleEditOpen: false,
          custom_corrected_title: trimmed,
          review_status: "accepted",
          suggested_title_confirmation: true,
          accepted_recorded_at: now,
          is_active_issue: false,
        }));
        void loadMetrics();
        void loadLatestRunSummary();
        toast.current?.show({
          severity: "success",
          summary: "Title saved",
          detail: "Your corrected title has been accepted.",
          life: 3200,
        });
      } catch (e) {
        patchRowByVideoId(vid, () => ({
          ...row,
          _savingTitleEdit: false,
          _titleEditOpen: true,
          _titleEditDraft: trimmed,
        }));
        toast.current?.show({
          severity: "error",
          summary: "Could not save title",
          detail: apiErrorDetail(e),
        });
      }
    },
    [
      blocklistEntries,
      deselectRowByVideoId,
      loadMetrics,
      loadLatestRunSummary,
      patchRowByVideoId,
      settleReviewedRow,
    ]
  );

  type TextRowField =
    | "run_id"
    | "video_id"
    | "channel_id"
    | "channel_title"
    | "channel_language"
    | "video_type"
    | "video_privacy_status"
    | "upload_time"
    | "vod_channel_manager_email"
    | "shorts_channel_manager_email"
    | "original_title"
    | "corrected_title"
    | "updated_at";

  const nullishBody = (field: TextRowField) => (row: MetadataSpellcheckRow) => {
    const v = row[field];
    if (v === null || v === undefined || v === "") return "—";
    return String(v);
  };

  const totalOpen = Math.max(
    0,
    metrics?.totalOpenIssues ?? 0
  );
  const filteredTotal = sortedRows.length;
  const defaultReviewFilterSelected =
    selectedReviewStatuses.length === 1 && selectedReviewStatuses[0] === "pending";
  const hasActiveFilters =
    auditActive ||
    !defaultReviewFilterSelected ||
    selectedLanguages.length > 0 ||
    selectedManagerEmails.length > 0 ||
    selectedVideoTypes.length > 0 ||
    selectedVideoPrivacyStatuses.length > 0 ||
    selectedCorrectionTypes.length > 0 ||
    selectedValidationStatuses.length > 0 ||
    selectedChannelKeys.length > 0 ||
    selectedUploadDates.length > 0;
  const yourIssuesCount =
    metrics?.totalMyOpenIssues ??
    (myIssuesOnly ? totalMatchingServer : undefined) ??
    myIssuesRows.length;

  const showTableSpinner = loadingInitial && allRows.length === 0;

  const tableColumnOrder = useMemo(() => {
    const optOrder = new Map(
      OPTIONAL_TITLE_SPELLING_TABLE_COLUMNS.map((c, i) => [c.id, i])
    );
    const extra = (optionalTableColumns as string[]).filter((id) => optOrder.has(id));
    extra.sort((a, b) => (optOrder.get(a) ?? 0) - (optOrder.get(b) ?? 0));
    return [...DEFAULT_TITLE_SPELLING_TABLE_COLUMNS, ...extra] as string[];
  }, [optionalTableColumns]);

  const renderSpellingDataColumn = (columnId: string) => {
    switch (columnId) {
      case "run_id":
        return (
          <Column
            key={columnId}
            field="run_id"
            header="Last run ID"
            body={(row: MetadataSpellcheckRow) => (
              <span className="title-spelling-cell-mono">{nullishBody("run_id")(row)}</span>
            )}
            style={{ minWidth: "120px" }}
          />
        );
      case "video_id":
        return (
          <Column
            key={columnId}
            field="video_id"
            header="Video ID"
            body={(row: MetadataSpellcheckRow) => (
              <span className="title-spelling-cell-mono">{row.video_id}</span>
            )}
            style={{ minWidth: "120px" }}
          />
        );
      case "channel_id":
        return (
          <Column
            key={columnId}
            field="channel_id"
            header="Channel ID"
            body={(row: MetadataSpellcheckRow) => (
              <span className="title-spelling-cell-mono">{nullishBody("channel_id")(row)}</span>
            )}
            style={{ minWidth: "120px" }}
          />
        );
      case "channel_title":
        return (
          <Column
            key={columnId}
            field="channel_title"
            header="Channel title"
            body={nullishBody("channel_title")}
            style={{ minWidth: "160px" }}
          />
        );
      case "channel_language":
        return (
          <Column
            key={columnId}
            field="channel_language"
            header="Channel language"
            body={nullishBody("channel_language")}
            style={{ minWidth: "120px" }}
          />
        );
      case "video_type":
        return (
          <Column
            key={columnId}
            field="video_type"
            header="Video type"
            body={nullishBody("video_type")}
            style={{ minWidth: "120px" }}
          />
        );
      case "video_privacy_status":
        return (
          <Column
            key={columnId}
            field="video_privacy_status"
            header="Privacy"
            headerTooltip="Video privacy status from VideoAssetMapping"
            body={(row: MetadataSpellcheckRow) => (
              <PrivacyStatusCell value={row.video_privacy_status} />
            )}
            style={{ minWidth: "110px" }}
          />
        );
      case "upload_time":
        return (
          <Column
            key={columnId}
            field="upload_time"
            header="Upload time"
            body={(row: MetadataSpellcheckRow) => formatUploadTimeDisplay(row.upload_time)}
            style={{ minWidth: "160px" }}
          />
        );
      case "priority_level":
        return (
          <Column
            key={columnId}
            field="priority_level"
            header="Priority"
            body={(row: MetadataSpellcheckRow) => {
              const level = getPriorityLevel(row);
              const isHigh = level === "high";
              return (
                <span
                  className={`title-spelling-priority-badge ${
                    isHigh
                      ? "title-spelling-priority-high"
                      : "title-spelling-priority-secondary"
                  }`}
                  title={
                    isHigh
                      ? "High priority: spelling correction detected"
                      : "Secondary priority: punctuation/capitalisation/spacing only"
                  }
                >
                  {isHigh ? "High" : "Secondary"}
                </span>
              );
            }}
            style={{ minWidth: "120px" }}
          />
        );
      case "vod_channel_manager_email":
        return (
          <Column
            key={columnId}
            field="vod_channel_manager_email"
            header="VOD mgr email"
            body={(row: MetadataSpellcheckRow) =>
              formatChannelManagerEmails(row.vod_channel_manager_email)
            }
            style={{ minWidth: "180px" }}
          />
        );
      case "shorts_channel_manager_email":
        return (
          <Column
            key={columnId}
            field="shorts_channel_manager_email"
            header="Shorts mgr email"
            body={(row: MetadataSpellcheckRow) =>
              formatChannelManagerEmails(row.shorts_channel_manager_email)
            }
            style={{ minWidth: "180px" }}
          />
        );
      case "original_title":
        return (
          <Column
            key={columnId}
            field="original_title"
            header="Original title"
            body={(row: MetadataSpellcheckRow) => {
              const o = row.original_title;
              const display = getDisplayCorrectedTitle(row);
              if (o === null || o === undefined || o === "") return "—";
              const orig = String(o);
              if (display === null || orig === display) {
                return orig;
              }
              const { originalSpans } = titleDiffSpans(orig, display);
              return (
                <span className="title-spelling-title-wrap">
                  {renderTitleWithDiff(originalSpans, "original")}
                </span>
              );
            }}
            style={{ minWidth: "220px" }}
          />
        );
      case "corrected_title":
        return (
          <Column
            key={columnId}
            field="corrected_title"
            header="Corrected title"
            body={(row: MetadataSpellcheckRow) => {
              const o = row.original_title;
              const display = getDisplayCorrectedTitle(row);
              if (display === null) return "—";
              const showManualReviewBadge = needsManualLengthReview(row);
              const returnedText = returnedForReviewText(row);
              const showNoSubstituteBadge = isNoSubstitute(row);
              const substitutedHits = !showNoSubstituteBadge
                ? resolveSubstitutedHitsForRow(row, blocklistEntries)
                : [];
              const showSubstitutedBadge = substitutedHits.length > 0;
              const manualReviewText = `Manual review: exceeds YouTube 100-char title limit${
                row.corrected_title_length != null ? ` (${row.corrected_title_length} chars)` : ""
              }`;
              const noSubstitutePhrases = showNoSubstituteBadge
                ? resolveNoSubstituteHitsForRow(row, blocklistEntries)
                    .map((h) => h.phrase)
                    .join(", ")
                : "";
              const noSubstituteText = noSubstitutePhrases
                ? `Prohibited term — no substitute defined (${noSubstitutePhrases})`
                : "Prohibited term — no substitute defined";
              const substitutedText = substitutedHits.length
                ? `Blocklist term auto-replaced (${substitutedHits.map((h) => h.phrase).join(", ")})`
                : "Blocklist term auto-replaced";
              let correctedTitleNode: React.ReactNode;
              if (o === null || o === undefined || o === "" || String(o) === display) {
                correctedTitleNode = display;
              } else {
                const { correctedSpans } = titleDiffSpans(String(o), display);
                correctedTitleNode = renderTitleWithDiff(correctedSpans, "corrected");
              }
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {returnedText ? (
                    <span className="title-spelling-manual-review-badge">{returnedText}</span>
                  ) : (
                    showManualReviewBadge && (
                      <span className="title-spelling-manual-review-badge">{manualReviewText}</span>
                    )
                  )}
                  {showNoSubstituteBadge && (
                    <span className="title-spelling-no-substitute-badge">{noSubstituteText}</span>
                  )}
                  {showSubstitutedBadge && (
                    <span className="title-spelling-blocklist-substituted-badge">
                      {substitutedText}
                    </span>
                  )}
                  <span className="title-spelling-title-wrap">{correctedTitleNode}</span>
                </div>
              );
            }}
            style={{ minWidth: "220px" }}
          />
        );
      case "review_status":
        return (
          <Column
            key={columnId}
            field="review_status"
            header="Review status"
            body={(row: MetadataSpellcheckRow) => {
              const status = getReviewStatus(row);
              return (
                <span className={reviewStatusBadgeClass(status)}>
                  {reviewStatusLabel(status)}
                </span>
              );
            }}
            style={{ minWidth: "140px" }}
          />
        );
      case "review_actions":
        return (
          <Column
            key={columnId}
            field="review_actions"
            header="Review actions"
            body={(row: MetadataSpellcheckRow) => {
              const rowActionsLocked = selectionLocked;
              const busy = savingTitleConfirmation[row.video_id] === true;
              const busyTitleEdit = row._savingTitleEdit === true;
              const status = getReviewStatus(row);
              const isPending = status === "pending";
              const selectedRejectionReason = getDraftedRejectionReason(row);
              const customRejectionReason = getDraftedCustomRejectionReason(row) ?? "";
              const customReasonRequired = selectedRejectionReason === "Other";
              const noSubstitute = isNoSubstitute(row);
              const titleEditActive = row._titleEditOpen === true;
              const titleEditDraft = row._titleEditDraft ?? titleEditPrefill(row);
              const titleEditOverLimit = titleEditDraft.trim().length > 100;
              const blocklistHit = findFirstBlocklistPhraseInTitle(
                titleEditDraft,
                blocklistEntriesToPhraseEntries(blocklistEntries)
              );
              const acceptBlocked = acceptBlockedReason(row, blocklistEntries);
              const saveBlocked = Boolean(
                blocklistHit || titleEditOverLimit || !titleEditDraft.trim()
              );
              return (
                <div
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    minWidth: "230px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                    <Button
                      type="button"
                      label="Accept correction"
                      className={
                        status === "accepted"
                          ? "p-button-success p-button-sm"
                          : "p-button-success p-button-outlined p-button-sm"
                      }
                      disabled={
                        busy ||
                        rowActionsLocked ||
                        Boolean(acceptBlocked) ||
                        titleEditActive
                      }
                      tooltip={
                        titleEditActive
                          ? "Close or save title edit before accepting"
                          : acceptBlocked
                      }
                      tooltipOptions={{ position: "top" }}
                      onClick={() => {
                        void onSaveTitleReview(row, true);
                      }}
                    />
                    <Button
                      type="button"
                      label="Reject correction"
                      className={
                        status === "rejected"
                          ? "p-button-danger p-button-sm"
                          : "p-button-danger p-button-outlined p-button-sm"
                      }
                      disabled={busy || rowActionsLocked}
                      onClick={() => {
                        void onSaveTitleReview(row, false);
                      }}
                    />
                    {isPending && !titleEditActive && (
                      <Button
                        type="button"
                        label="Edit title"
                        icon="pi pi-pencil"
                        className="p-button-outlined p-button-sm title-spelling-edit-title-btn"
                        disabled={busy || rowActionsLocked}
                        tooltip="Edit the title before accepting"
                        tooltipOptions={{ position: "top" }}
                        onClick={() => handleOpenTitleEdit(row)}
                      />
                    )}
                  </div>
                  {isPending && titleEditActive && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <InputText
                        value={titleEditDraft}
                        onChange={(e) => {
                          if (selectionLockedRef.current) return;
                          patchRowByVideoId(row.video_id, (r) => ({
                            ...r,
                            _titleEditDraft: e.target.value,
                          }));
                        }}
                        className={
                          titleEditOverLimit ? "title-spelling-title-edit-input-over" : undefined
                        }
                        disabled={busyTitleEdit || rowActionsLocked}
                        placeholder="Enter corrected title"
                      />
                      {noSubstitute && (
                        <span className="title-spelling-title-edit-hint">
                          Remove prohibited term(s) from the title — adding allowlisted brand names
                          does not override blocklist rules.
                        </span>
                      )}
                      {blocklistHit && (
                        <span className="title-spelling-title-edit-error">
                          Remove prohibited term: «{blocklistHit}»
                        </span>
                      )}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <span
                          className={
                            titleEditOverLimit
                              ? "title-spelling-title-edit-count title-spelling-title-edit-count-over"
                              : "title-spelling-title-edit-count"
                          }
                        >
                          {titleEditDraft.trim().length} / 100
                        </span>
                        <div style={{ display: "flex", gap: "4px" }}>
                          <Button
                            type="button"
                            label="Cancel"
                            className="p-button-outlined p-button-sm title-spelling-title-edit-cancel-btn"
                            disabled={busyTitleEdit || rowActionsLocked}
                            onClick={() =>
                              patchRowByVideoId(row.video_id, (r) => ({
                                ...r,
                                _titleEditOpen: false,
                              }))
                            }
                          />
                          <Button
                            type="button"
                            label="Save & Accept"
                            size="small"
                            className="p-button-success p-button-sm"
                            disabled={busyTitleEdit || rowActionsLocked || saveBlocked}
                            loading={busyTitleEdit}
                            onClick={() => void onSaveEditedTitle(row, titleEditDraft)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <Dropdown
                    value={selectedRejectionReason}
                    options={REJECTION_REASON_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    showClear
                    placeholder="Optional rejection reason"
                    disabled={busy || rowActionsLocked}
                    onChange={(e) => {
                      if (selectionLockedRef.current) return;
                      const nextValue = typeof e.value === "string" ? e.value : "";
                      patchRowByVideoId(row.video_id, (current) => ({
                        ...current,
                        _rejectionReasonDraft: nextValue,
                        ...(nextValue !== "Other"
                          ? { _customRejectionReasonDraft: undefined }
                          : {}),
                      }));
                    }}
                    style={{ width: "100%" }}
                  />
                  {customReasonRequired && (
                    <InputText
                      value={customRejectionReason}
                      placeholder="Enter custom rejection reason"
                      maxLength={MAX_CUSTOM_REJECTION_REASON_LENGTH}
                      disabled={busy || rowActionsLocked}
                      onChange={(e) => {
                        if (selectionLockedRef.current) return;
                        const nextValue = e.target.value ?? "";
                        patchRowByVideoId(row.video_id, (current) => ({
                          ...current,
                          _customRejectionReasonDraft: nextValue,
                        }));
                      }}
                      style={{ width: "100%" }}
                    />
                  )}
                </div>
              );
            }}
            style={{ minWidth: "280px" }}
          />
        );
      case "apply_status":
        return (
          <Column
            key={columnId}
            field="apply_status"
            header="Apply status"
            body={(row: MetadataSpellcheckRow) => {
              const status = applyStatusLabel(row, isAdmin);
              return (
                <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  {status === "Failed" ? (
                    <div className="title-spelling-apply-failed-cell">
                      <span
                        className="title-spelling-status-badge title-spelling-apply-failed"
                        title={row.youtube_apply_last_error ?? "YouTube update failed"}
                      >
                        Failed
                      </span>
                      <span className="title-spelling-apply-failed-reason">
                        {applyFailureReason(row.youtube_apply_last_error)}
                      </span>
                    </div>
                  ) : status === "Queue" ? (
                  <span className="title-spelling-status-badge title-spelling-apply-queue">
                    Queue
                  </span>
                  ) : status === "Updated" ? (
                  <span className="title-spelling-status-badge title-spelling-apply-updated">
                    Updated
                  </span>
                  ) : (
                    "—"
                  )}
                </div>
              );
            }}
            style={{ minWidth: "120px" }}
          />
        );
      case "corrections":
        return (
          <Column
            key={columnId}
            field="corrections"
            header="Correction types"
            body={(row: MetadataSpellcheckRow) => {
              const list = row.corrections;
              if (!list || list.length === 0) return "—";
              return <span className="title-spelling-corrections">{list.join(", ")}</span>;
            }}
            style={{ minWidth: "140px" }}
          />
        );
      case "updated_at":
        return (
          <Column
            key={columnId}
            field="updated_at"
            header="Updated"
            body={(row: MetadataSpellcheckRow) => formatTimestampDisplay(row.updated_at)}
            style={{ minWidth: "160px" }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="title-spelling-tab">
      <Toast ref={toast} />
      <ConfirmDialog />
      <Dialog
        header="Bulk reject — shared reason"
        visible={bulkRejectDialogVisible}
        onHide={() => {
          if (bulkBusy) return;
          resetBulkRejectDialog();
        }}
        style={{ width: "420px", maxWidth: "95vw" }}
        footer={
          <div className="title-spelling-bulk-reject-dialog-footer">
            <Button
              type="button"
              label="Cancel"
              className="p-button-text"
              onClick={resetBulkRejectDialog}
            />
            <Button
              type="button"
              label="Continue"
              className="p-button-danger"
              disabled={liveSelectedPendingCount === 0 || bulkBusy}
              onClick={handleBulkRejectConfirm}
            />
          </div>
        }
      >
        <p className="title-spelling-bulk-reject-dialog-intro">
          Optional rejection reason applied to all {liveSelectedPendingCount} selected issue
          {liveSelectedPendingCount === 1 ? "" : "s"}.
        </p>
        <Dropdown
          value={bulkRejectReason}
          options={REJECTION_REASON_OPTIONS}
          optionLabel="label"
          optionValue="value"
          showClear
          placeholder="Optional rejection reason"
          onChange={(e) => {
            const nextValue = typeof e.value === "string" ? e.value : null;
            setBulkRejectReason(nextValue);
            if (nextValue !== "Other") {
              setBulkRejectCustomReason("");
            }
          }}
          className="title-spelling-bulk-reject-reason"
        />
        {bulkRejectReason === "Other" && (
          <div className="title-spelling-bulk-reject-custom-field">
            <InputText
              value={bulkRejectCustomReason}
              placeholder="Enter custom rejection reason"
              maxLength={MAX_CUSTOM_REJECTION_REASON_LENGTH}
              onChange={(e) => setBulkRejectCustomReason(e.target.value ?? "")}
              className="title-spelling-bulk-reject-custom"
              aria-describedby="bulk-reject-custom-char-count"
            />
            <div
              id="bulk-reject-custom-char-count"
              className={`title-spelling-bulk-reject-char-count${
                bulkRejectCustomReason.length >= MAX_CUSTOM_REJECTION_REASON_LENGTH
                  ? " title-spelling-bulk-reject-char-count--at-limit"
                  : ""
              }`}
              aria-live="polite"
            >
              {bulkRejectCustomReason.length}/{MAX_CUSTOM_REJECTION_REASON_LENGTH} characters
            </div>
          </div>
        )}
      </Dialog>
      {pendingBulkJob && !bulkExecuting && (
        <div className="title-spelling-bulk-pending-toast" role="status" aria-live="polite">
          <div className="title-spelling-bulk-pending-toast-header">
            <span className="title-spelling-bulk-pending-toast-title">
              Bulk {pendingBulkJob.kind === "accept" ? "accept" : "reject"} scheduled
            </span>
            <span className="title-spelling-bulk-pending-toast-count">
              {pendingBulkJob.videoIds.length} issue
              {pendingBulkJob.videoIds.length === 1 ? "" : "s"}
            </span>
          </div>
          <p className="title-spelling-bulk-pending-toast-detail">
            Applying when countdown finishes. You can undo before then.
          </p>
          <div
            key={bulkPendingKey}
            className="title-spelling-bulk-pending-progress"
            style={
              {
                "--bulk-countdown-ms": `${BULK_COUNTDOWN_MS}ms`,
              } as React.CSSProperties
            }
            aria-hidden
          >
            <div className="title-spelling-bulk-pending-progress-fill" />
          </div>
          <Button
            type="button"
            label="Undo"
            className="p-button-outlined p-button-sm title-spelling-bulk-pending-undo"
            onClick={cancelPendingBulk}
          />
        </div>
      )}
      <div className="title-spelling-page-wrap">
        <div className="title-spelling-kpi-row">
          <div className="title-spelling-kpi-slot-wide">
            <KpiCard
              label="Open issues"
              value={formatNumber(metrics?.totalOpenIssues ?? 0)}
              subValue="Awaiting accept or reject decision"
              accentColor="#6366f1"
            />
          </div>
          <div className="title-spelling-kpi-slot">
            <KpiCard
              label="New issues"
              value={formatNumber(latestRunSummary.newIssues)}
              subValue={
                latestRunSummary.runId
                  ? `From latest run ${latestRunSummary.runId}`
                  : "From latest run"
              }
              accentColor="#2563eb"
            />
          </div>
          <div className="title-spelling-kpi-slot">
            <KpiCard
              label="Your issues"
              value={formatNumber(yourIssuesCount)}
              subValue="Assigned to your channel manager email"
              accentColor="#d97706"
            />
          </div>
          <div className="title-spelling-kpi-slot">
            <KpiCard
              label="Accepted issues"
              value={formatNumber(metrics?.totalAcceptedIssues ?? 0)}
              subValue="Total reviewer acceptances"
              accentColor="#16a34a"
            />
          </div>
          <div className="title-spelling-kpi-slot">
            <KpiCard
              label="Rejected issues"
              value={formatNumber(metrics?.totalRejectedIssues ?? 0)}
              subValue="Total reviewer rejections"
              accentColor="#dc2626"
            />
          </div>
        </div>

        {error && <div className="title-spelling-error-banner">{error}</div>}

        <Divider style={{ margin: 0 }} />

        <div>
          <div
            id="metadata-spellcheck-filters"
            className="grid-container"
            style={{ marginBottom: "14px" }}
          >
            <div className="filter-item">
              <div className="title-spelling-filter-label">Review state</div>
              <MultiSelect
                value={auditActive ? AUDIT_REVIEW_STATUSES : selectedReviewStatuses}
                // The failed-update audit always covers every review state.
                disabled={auditActive}
                options={REVIEW_STATUS_OPTIONS}
                onChange={(e) => {
                  const values = Array.isArray(e.value)
                    ? e.value.filter(
                        (v): v is "pending" | "accepted" | "rejected" =>
                          v === "pending" || v === "accepted" || v === "rejected"
                      )
                    : [];
                  setSelectedReviewStatuses(values.length > 0 ? values : ["pending"]);
                }}
                optionLabel="label"
                optionValue="value"
                placeholder="Pending review only"
                display="chip"
                showClear
                maxSelectedLabels={2}
                style={{ width: "90%", borderRadius: "5px" }}
              />
            </div>
            <div className="filter-item">
              <div className="title-spelling-filter-label">
                Channel (title + ID)
              </div>
              <MultiSelect
                value={selectedChannelKeys}
                options={channelOptions}
                onChange={(e) => setSelectedChannelKeys(e.value ?? [])}
                optionLabel="label"
                optionValue="value"
                placeholder="All channels"
                display="chip"
                filter
                showClear
                maxSelectedLabels={1}
                style={{ width: "90%", borderRadius: "5px" }}
              />
            </div>
            <div className="filter-item">
              <div className="title-spelling-filter-label">Channel language</div>
              <MultiSelect
                value={selectedLanguages}
                options={languageOptions}
                onChange={(e) => setSelectedLanguages(e.value ?? [])}
                optionLabel="label"
                optionValue="value"
                placeholder="All languages"
                display="chip"
                filter
                showClear
                maxSelectedLabels={2}
                style={{ width: "90%", borderRadius: "5px" }}
              />
            </div>
            <div className="filter-item">
              <div className="title-spelling-filter-label">Video type</div>
              <MultiSelect
                value={selectedVideoTypes}
                options={videoTypeOptions}
                onChange={(e) => setSelectedVideoTypes(e.value ?? [])}
                optionLabel="label"
                optionValue="value"
                placeholder="All types"
                display="chip"
                filter
                showClear
                maxSelectedLabels={2}
                style={{ width: "90%", borderRadius: "5px" }}
              />
            </div>
            <div className="filter-item">
              <div className="title-spelling-filter-label">Video privacy</div>
              <MultiSelect
                value={selectedVideoPrivacyStatuses}
                options={videoPrivacyOptions}
                onChange={(e) => setSelectedVideoPrivacyStatuses(e.value ?? [])}
                optionLabel="label"
                optionValue="value"
                placeholder="All privacy statuses"
                display="chip"
                filter
                showClear
                maxSelectedLabels={2}
                style={{ width: "90%", borderRadius: "5px" }}
              />
            </div>
            <div className="filter-item">
              <div className="title-spelling-filter-label">
                Upload date (UTC)
              </div>
              <MultiSelect
                value={selectedUploadDates}
                options={uploadDateOptions}
                onChange={(e) => setSelectedUploadDates(e.value ?? [])}
                optionLabel="label"
                optionValue="value"
                placeholder="All upload dates"
                display="chip"
                filter
                showClear
                maxSelectedLabels={2}
                style={{ width: "90%", borderRadius: "5px" }}
              />
            </div>
            <div className="filter-item">
              <div className="title-spelling-filter-label">Correction types</div>
              <MultiSelect
                value={selectedCorrectionTypes}
                options={correctionTypeOptions}
                onChange={(e) => setSelectedCorrectionTypes(e.value ?? [])}
                optionLabel="label"
                optionValue="value"
                placeholder="All correction types"
                display="chip"
                filter
                showClear
                maxSelectedLabels={2}
                style={{ width: "90%", borderRadius: "5px" }}
              />
            </div>
            <div className="filter-item">
              <div className="title-spelling-filter-label">Manual review</div>
              <MultiSelect
                value={selectedValidationStatuses}
                options={VALIDATION_STATUS_OPTIONS}
                onChange={(e) => setSelectedValidationStatuses(e.value ?? [])}
                optionLabel="label"
                optionValue="value"
                placeholder="All validation states"
                display="chip"
                showClear
                maxSelectedLabels={1}
                style={{ width: "90%", borderRadius: "5px" }}
              />
            </div>
            {isAdmin && (
              <div className="filter-item">
                <div className="title-spelling-filter-label">Admin audit</div>
                <label className="title-spelling-audit-toggle">
                  <InputSwitch
                    checked={auditFailedUpdates}
                    onChange={(e) => setAuditFailedUpdates(Boolean(e.value))}
                  />
                  <span>Failed YouTube updates</span>
                </label>
              </div>
            )}
            <div className="filter-item">
              <div className="title-spelling-filter-label">Channel manager</div>
              <MultiSelect
                value={selectedManagerEmails}
                options={channelManagerOptionsFromIssues}
                onChange={(e) => {
                  const values = (e.value ?? []) as string[];
                  setSelectedManagerEmails(values);
                  if (values.length > 0) {
                    setMyIssuesOnly(false);
                  }
                }}
                optionLabel="label"
                optionValue="value"
                placeholder="All managers"
                display="chip"
                filter
                showClear
                maxSelectedLabels={1}
                style={{ width: "90%", borderRadius: "5px" }}
              />
            </div>
            <div className="filter-item">
              <div className="title-spelling-filter-label">Additional columns</div>
              <MultiSelect
                value={optionalTableColumns}
                options={OPTIONAL_TITLE_SPELLING_TABLE_COLUMNS}
                onChange={(e) => setOptionalTableColumns(e.value ?? [])}
                optionLabel="label"
                optionValue="id"
                placeholder="Select columns to add"
                display="chip"
                filter
                showClear
                maxSelectedLabels={2}
                style={{ width: "90%", borderRadius: "5px" }}
              />
            </div>
          </div>
          <div className="grid-container" style={{ marginBottom: "10px" }}>
            <div className="flex-row grid gap-5" style={{ marginLeft: "0.1vh" }}>
              <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
              <Button
                type="button"
                label="Clear filters"
                icon="pi pi-filter-slash"
                className="p-button-outlined p-button-sm"
                disabled={!hasActiveFilters}
                onClick={clearFilters}
              />
              </div>
              <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
                <Button
                  type="button"
                  label={
                    updatedSortOrder === "desc"
                      ? "Updated: newest first"
                      : "Updated: oldest first"
                  }
                  icon={
                    updatedSortOrder === "desc"
                      ? "pi pi-sort-amount-down"
                      : "pi pi-sort-amount-up-alt"
                  }
                  className="p-button-text p-button-sm"
                  disabled={loadingInitial}
                  onClick={onToggleUpdatedSortOrder}
                />
              </div>
              <div className="your-issues" title={`${formatNumber(yourIssuesCount)} issues assigned`}>
                <div className="filter-item">
                  <span className="title-spelling-filter-label">Your Issues</span>
                </div>
                <div>
                  <InputSwitch
                    checked={myIssuesOnly}
                    disabled={!loggedInUserEmail}
                    onChange={(e) => onMyIssuesOnlyChange(Boolean(e.value))}
                  />
                </div>
              </div>
              <div className="title-spelling-bulk-toolbar">
                <Button
                  type="button"
                  label="Accept Bulk"
                  severity="success"
                  className="p-button-sm"
                  disabled={liveSelectedPendingCount === 0 || bulkBusy}
                  onClick={handleBulkAccept}
                />
                <Button
                  type="button"
                  label="Reject Bulk"
                  severity="danger"
                  className="p-button-sm"
                  disabled={liveSelectedPendingCount === 0 || bulkBusy}
                  onClick={() => {
                    setBulkRejectReason(null);
                    setBulkRejectCustomReason("");
                    setBulkRejectDialogVisible(true);
                  }}
                />
                {liveSelectedPendingCount > 0 && (
                  <div className="title-spelling-bulk-selection-meta">
                    <span className="title-spelling-bulk-selected-count">
                      {liveSelectedPendingCount} selected
                    </span>
                    <Button
                      type="button"
                      label="Clear selected"
                      icon="pi pi-times"
                      className="p-button-text p-button-sm title-spelling-bulk-clear-selected"
                      disabled={selectionLocked}
                      onClick={clearSelection}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="last-item">
              <div
                className="flex-column align-center justify-center"
                style={{ gap: "8px", marginTop: "1rem" }}
              >
                <Button
                  label="CSV (open issues)"
                  icon="pi pi-download"
                  className="p-button-outlined p-button-sm"
                  onClick={() => {
                    trackToolAction("MetadataAudit", "download");
                    void metadataSpellcheckApi.downloadOpenIssuesCsv();
                  }}
                />
                <Button
                  label="CSV (accepted issues)"
                  icon="pi pi-download"
                  className="p-button-outlined p-button-sm"
                  onClick={() => {
                    trackToolAction("MetadataAudit", "download");
                    void metadataSpellcheckApi.downloadAcceptedIssuesCsv();
                  }}
                />
                <Button
                  label="CSV (rejected issues)"
                  icon="pi pi-download"
                  className="p-button-outlined p-button-sm"
                  onClick={() => {
                    trackToolAction("MetadataAudit", "download");
                    void metadataSpellcheckApi.downloadRejectedIssuesCsv();
                  }}
                />
              </div>
            </div>
          </div>
          <div style={{ margin: "16px 0 12px" }}>
            <p className="title-spelling-info-heading">
              Metadata issues
              <span className="title-spelling-info-muted">
                {formatNumber(totalOpen)} open in Firestore · loaded {formatNumber(loadedCount)}
                {myIssuesOnly && totalMatchingServer !== undefined
                  ? ` · ${formatNumber(totalMatchingServer)} yours (server)`
                  : ""}
                {loadCapReached ? " (load cap)" : ""} · {formatNumber(filteredTotal)} after filters · {PAGE_SIZE} per page
              </span>
            </p>
            <p className="title-spelling-info-body">
              Title QC issues from Firestore{" "}
              <code style={{ fontSize: "12px" }}>metadataSpellcheckIssues</code>. Default view shows pending/open
              items; use <strong>Review state</strong> to include accepted/rejected rows.{" "}
              <strong>Last run ID</strong> is the pipeline run that last updated each document.
            </p>
            <p className="title-spelling-info-body">
              Filters are applied on the server; load more to page through matching issues (up to{" "}
              {formatNumber(MAX_LOADED_ISSUES)} held in memory). Filter dropdown values come from open issues in
              Firestore (bounded scan). Upload date uses the
              asset upload time (UTC calendar day, <code style={{ fontSize: "12px" }}>YYYY-MM-DD</code>). Correction
              type matches if a row has <strong>any</strong> of the selected types.
            </p>
            <p className="title-spelling-info-body-spaced">
              YouTube titles are limited to 100 characters. Rows flagged for length validation are shown as manual
              review issues before apply.
            </p>
          </div>
          {showTableSpinner ? (
            <Card>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <ProgressSpinner />
              </div>
            </Card>
          ) : (
            <Card>
              <div className="title-spelling-table-shell">
                {bulkBusy && (
                  <div
                    className="title-spelling-table-busy-shield"
                    role="status"
                    aria-label="Bulk review in progress — table actions paused"
                  />
                )}
                <DataTable
                  value={pageRows}
                selection={selectedRows}
                onSelectionChange={handleSelectionChange}
                selectionMode="multiple"
                dataKey="video_id"
                metaKeySelection={false}
                onRowClick={handleRowClick}
                isDataSelectable={isRowSelectable}
                rowClassName={spellingRowClassName}
                loading={(loadingInitial || loadingMore) && !showTableSpinner}
                emptyMessage="No issues match the selected filters"
                stripedRows
                size="small"
                scrollable
                scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
              >
                {tableColumnOrder.map((id) => renderSpellingDataColumn(id))}
              </DataTable>
              </div>
              <div
                style={{
                  marginTop: "12px",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {issuesHasMore && !loadCapReached && (
                  <Button
                    type="button"
                    label={loadingMore ? "Loading…" : "Load more issues"}
                    icon={loadingMore ? "pi pi-spin pi-spinner" : "pi pi-plus"}
                    className="p-button-outlined p-button-sm"
                    disabled={loadingMore || loadingInitial}
                    onClick={() => {
                      interruptBulkWorkflow();
                      void loadMoreIssues();
                    }}
                  />
                )}
                {loadCapReached && (
                  <span className="title-spelling-load-cap">
                    Loaded {formatNumber(MAX_LOADED_ISSUES)} rows (cap). Narrow filters or use CSV export for full data.
                  </span>
                )}
                {filteredTotal > 0 && (
                  <Paginator
                    first={pageIndex * PAGE_SIZE}
                    rows={PAGE_SIZE}
                    totalRecords={filteredTotal}
                    onPageChange={onPage}
                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                    currentPageReportTemplate="{first}–{last} of {totalRecords}"
                  />
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TitleSpellingTab;
